import type * as React from "react";

import { cn } from "../lib/cn";

/*
 * Slip paper: the stopped-red highlighter lightened over card stock, so this
 * is real pink paper (opaque), not a wash - and dark ink stays comfortably AA
 * on it (espresso on this mix is about 11.7:1).
 */
const SLIP_PAPER =
  "bg-[color-mix(in_srgb,var(--color-wash-stopped)_40%,var(--color-card))]";

/*
 * Ruling like IndexCard's, but pink-tinted and on a smaller period. Message
 * type is 0.875rem (14px) with line-height 1.5em (21px), so the rule pattern
 * shares that 21px period: the stroke is the last 1px of each tile. With the
 * body's pt-1, the first baseline lands about 19.6px below the padding-box top
 * (Inter metrics), so the first stroke at 21px sits just under it, the way
 * handwriting sits on a real pad. Override font-size or leading together or
 * not at all.
 */
const SLIP_RULING = [
  "[background-image:repeating-linear-gradient(transparent,transparent_calc(1.5em_-_1px),color-mix(in_srgb,var(--color-ink-stopped)_40%,var(--color-card))_calc(1.5em_-_1px),color-mix(in_srgb,var(--color-ink-stopped)_40%,var(--color-card))_1.5em)]",
  "[background-size:100%_1.5em]",
].join(" ");

export interface MessageSlipProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role"
> {
  /** One or two short sentences: what stopped, and what to do about it. */
  message: React.ReactNode;
  /** The whole slip is one dismiss button; any click lands here. */
  onDismiss: () => void;
  /** Applied to the outer alert element. Positioning belongs to the caller. */
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The pink "WHILE YOU WERE OUT" slip: a single persistent failure notice,
 * written down and left where you will see it. It never auto-dismisses, and
 * the entire slip is one button that dismisses it - there are no nested
 * interactive elements.
 *
 * The caller owns positioning and lifecycle:
 * - place it via className, typically `fixed right-4 bottom-4 z-toast`;
 * - show one at a time - a new failure replaces the old slip;
 * - remount with a fresh `key` per message so the entrance replays and the
 *   alert re-announces.
 *
 * Accessibility: the outer element is the `role="alert"` live region, so
 * mounting announces the message. The pad chrome (header, taken-by corner) is
 * decorative and hidden from AT; the button's accessible name is the message
 * itself plus a screen-reader-only "Dismiss".
 *
 * Native props land on that outer alert element. `role` is not one of them -
 * the live region is the component, not a default a caller may swap out.
 */
export function MessageSlip({
  message,
  onDismiss,
  className,
  ...rest
}: MessageSlipProps) {
  return (
    <div {...rest} role="alert" className={className}>
      <button
        type="button"
        onClick={onDismiss}
        className={cn(
          /* Sharp corners on purpose: real slips are not rounded. It rests
             level: a slip carries a message to be read, and the slap-in is
             where the paper physics live. */
          "block w-72 px-3.5 pt-2.5 pb-2 text-left",
          SLIP_PAPER,
          "shadow-lift-lg",
          /* fill-mode backwards, not both: a filled-forwards animation would pin
             translate at 0 forever and eat the hover lift. The 100% frame matches
             the resting classes, so reverting to them at the end is seamless. */
          "[animation:bds-slip-in_220ms_var(--ease-paper)_backwards] motion-reduce:animate-none",
          "transition duration-150 ease-paper motion-reduce:transition-none",
          "hover:-translate-y-px hover:shadow-float",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        )}
      >
        <span aria-hidden="true" className="block">
          <span className="block text-center text-annotation-caps font-semibold text-ink-stopped">
            While you were out
          </span>
          {/* The pad's thin double rule: two hairlines out of one 4px box. */}
          <span className="mt-1.5 block h-1 border-y border-ink-stopped/40" />
        </span>

        <span
          className={cn(
            "block min-h-12 pt-1 pb-0.5 font-body text-[0.875rem] leading-[1.5em] text-ink",
            SLIP_RULING,
          )}
        >
          {message}
        </span>

        <span
          aria-hidden="true"
          className="mt-1 block text-right font-mono text-[0.625rem] tracking-[0.08em] text-annotation"
        >
          - Brian
        </span>

        <span className="sr-only">Dismiss</span>
      </button>
    </div>
  );
}
